/**
 * CSV export for the operations dashboard's active tab. Alarms/resolved
 * export re-fetches the full result set from the same odl_app resource the
 * grid reads (paginating past the on-screen page so the export matches
 * whatever's currently filtered/sorted), while the maintenance tab exports
 * its already-loaded client-side rows.
 *
 * Contains:
 * - useOperationsExport — default export
 */

import { useCallback, useState } from 'react';

import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { fetchResourcePage } from '@sophocles/features/dashboards/api';
import { downloadCsv, rowsToCsv } from '@sophocles/utils/csvExport';

import { buildOperationsColumns } from '../components/operationsColumns';

const EXPORT_PAGE_SIZE = 500;

/**
 * @param {object} opts
 * @param {string} opts.activeTab - 'alarms' | 'maintenance'.
 * @param {string} opts.alarmStatus - 'all' | 'active' | 'resolved'; 'resolved' swaps the
 *   alarms tab's export to the resolved repair-log resource.
 * @param {object[]} opts.filteredMaintenance - maintenance tab's already-loaded rows.
 * @param {object[]} opts.maintenanceColumns - maintenance tab's DataGridPro columns.
 * @param {object} opts.alarmsExtraParams - current alarms-tab query params.
 * @param {object} opts.resolvedExtraParams - current resolved-tab query params.
 * @param {{current: {current: {getSortModel: Function}}}} opts.alarmsGridApiRef
 * @param {{current: {current: {getSortModel: Function}}}} opts.resolvedGridApiRef
 * @param {number|string} opts.customerId
 * @param {{siteGroup: string, site: string}} opts.terms
 * @param {Function} opts.tAlarm
 * @param {Function} opts.t - i18next translator.
 * @param {object} opts.user
 * @param {Function} opts.stripTypeColumn - drops `structure_type` for non-billboard tenants.
 * @param {object} opts.alarmsColumnVisibility
 * @param {object} opts.resolvedColumnVisibility
 * @param {object} opts.maintenanceColumnVisibility
 * @returns {{exporting: boolean, handleExport: Function}}
 */
export default function useOperationsExport({
  activeTab,
  alarmStatus,
  filteredMaintenance,
  maintenanceColumns,
  alarmsExtraParams,
  resolvedExtraParams,
  alarmsGridApiRef,
  resolvedGridApiRef,
  customerId,
  terms,
  tAlarm,
  t,
  user,
  stripTypeColumn,
  alarmsColumnVisibility,
  resolvedColumnVisibility,
  maintenanceColumnVisibility,
}) {
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      let allRows;
      let columns;
      let fileName;

      const isResolved = activeTab === 'alarms' && alarmStatus === 'resolved';

      if (activeTab === 'maintenance') {
        allRows = filteredMaintenance;
        columns = maintenanceColumns;
        fileName = 'operations-maintenance.csv';
      } else {
        const basePath = isResolved ? 'operations-resolved' : 'operations-alarms';
        const extraP = isResolved ? resolvedExtraParams : alarmsExtraParams;
        const capturedApiRef = isResolved ? resolvedGridApiRef.current : alarmsGridApiRef.current;
        const sortModel = capturedApiRef?.current?.getSortModel?.() ?? [];
        const baseParams = { ...extraP };
        if (sortModel.length > 0) {
          baseParams.o = sortModel.map(({ field, sort }) => (sort === 'desc' ? `-${field}` : field)).join(',');
        }
        allRows = [];
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const wireParams = toJsonApiParams({ ...baseParams, limit: EXPORT_PAGE_SIZE, offset });
          const data = await fetchResourcePage(basePath, wireParams);
          const envelope = data?.payload ?? data;
          const rows = envelope?.results ?? envelope?.data ?? [];
          allRows.push(...rows);
          hasMore = rows.length === EXPORT_PAGE_SIZE;
          offset += EXPORT_PAGE_SIZE;
        }
        columns = stripTypeColumn(buildOperationsColumns({ resolved: isResolved, customerId, terms, tAlarm, t, user }));
        fileName = isResolved ? 'operations-resolved.csv' : 'operations-alarms.csv';
      }

      // Build CSV from visible columns + export-only columns
      const visibleCols = columns.filter(c => {
        if (c.exportOnly) return true;
        const vis =
          activeTab === 'maintenance'
            ? maintenanceColumnVisibility
            : isResolved
              ? resolvedColumnVisibility
              : alarmsColumnVisibility;
        return vis[c.field] !== false;
      });
      const csvColumns = visibleCols.map(c => ({
        headerName: c.headerName,
        valueGetter: row => {
          let val = c.valueGetter ? c.valueGetter(row[c.field], row) : row[c.field];
          if (c.valueFormatter && val != null) {
            val = c.valueFormatter(val);
          }
          return val;
        },
      }));
      const csv = rowsToCsv(csvColumns, allRows, { bom: true });
      downloadCsv(fileName, csv);
    } finally {
      setExporting(false);
    }
  }, [
    exporting,
    activeTab,
    alarmStatus,
    filteredMaintenance,
    maintenanceColumns,
    alarmsExtraParams,
    resolvedExtraParams,
    alarmsGridApiRef,
    resolvedGridApiRef,
    customerId,
    terms,
    tAlarm,
    t,
    user,
    stripTypeColumn,
    alarmsColumnVisibility,
    resolvedColumnVisibility,
    maintenanceColumnVisibility,
  ]);

  return { exporting, handleExport };
}
